import { textileRecycling } from "./textile-recycling";

export type ServiceSection = {
  heading: string;
  body?: string;
  bullets?: string[];
  link?: { label: string; href: string };
};

export type Service = {
  slug: string;
  n: string;
  title: string;
  tagline: string;
  short: string;
  metaDescription: string;
  highlights: string[];
  sections: ServiceSection[];
};

export const services: Service[] = [
  {
    slug: "textile-recycling",
    n: "01",
    title: "Textile Recycling",
    tagline: textileRecycling.intro,
    short:
      "Branded uniforms, workwear and end-of-life textiles diverted from landfill through careful sorting and recovery, with every collection logged and accounted for.",
    metaDescription: textileRecycling.description,
    highlights: [],
    sections: [],
  },
  {
    slug: "confidential-waste-disposal",
    n: "02",
    title: "Confidential Waste Disposal",
    tagline:
      "Secure confidential waste disposal and document shredding for organisations across the UK, designed around UK GDPR.",
    short:
      "GDPR-compliant paper shredding. We collect your documents, transport them securely and shred them off-site at a secure facility, with an electronic note of secure destruction.",
    metaDescription:
      "Confidential waste disposal and document shredding for UK organisations. Lockable consoles, scheduled or one-off collections and proof of destruction.",
    highlights: [
      "UK GDPR + Data Protection Act 2018",
      "Lockable on-site consoles",
      "Scheduled or one-off collections",
      "Electronic note of secure destruction"
    ],
    sections: [
      {
        heading: "What counts as confidential waste?",
        body: "Confidential waste is any material containing sensitive or private information: documents, files, records, digital media or physical items containing personal, financial, legal or other confidential data. Handling it securely prevents data breaches, identity theft and unauthorised disclosure."
      },
      {
        heading: "Which documents do you shred?",
        body: "If it identifies a person, a customer or your business, it belongs in a confidential waste console rather than a recycling bin. Typical examples include:",
        bullets: [
          "Employee records",
          "Sales data",
          "Payroll information",
          "Patient health records",
          "Accounting data",
          "Customer information",
          "Payment details"
        ]
      },
      {
        heading: "How does a regular shredding service work?",
        body: "We assess the volume of confidential waste your organisation generates, recommend the right console size and number, and agree a collection frequency that keeps your site clear and secure. Consoles are lockable and QR-code tracked, and are collected when full."
      },
      {
        heading: "Can we book a single collection instead?",
        body: "Yes. If you are clearing an archive, moving office or catching up on a backlog, we can collect as a one-off with no contract required.",
        link: { label: "See our one-off shredding service", href: "/services/one-off-shredding" }
      },
      {
        heading: "What proof of destruction will we receive?",
        body: "An electronic note of secure destruction is issued the working day after collection. It details the quantity and type of waste collected, and confirmation it has been securely destroyed."
      },
      {
        heading: "Which sectors is this service for?",
        body: "Any organisation that handles personal or commercially sensitive paperwork. It is well suited to:",
        bullets: [
          "Healthcare: patient records, referral letters, appointment lists and staff files.",
          "Security: incident reports, site rotas, client contracts and staff vetting paperwork.",
          "Logistics: delivery notes, manifests, driver records and customer details.",
          "Hospitality: guest registration cards, booking records, card payment slips and HR files.",
          "Education, finance and legal teams with records containing personal or financial data."
        ]
      },
      {
        heading: "How does this help with UK GDPR?",
        body: "UK GDPR expects you to keep personal data secure, including when you dispose of it. Lockable consoles, secure destruction and a written record of each collection help you show how your confidential waste was handled.",
        link: { label: "Read our guide to what the ICO expects", href: "/blog/ico-confidential-waste-disposal-guide" }
      }
    ],
  },
  {
    slug: "it-and-media-recycling",
    n: "03",
    title: "IT & Media Recycling",
    tagline:
      "Secure IT and media recycling for organisations across the UK: secure collection, data destruction and recycling in line with the WEEE Regulations.",
    short:
      "Secure decommissioning of hard drives, redundant hardware and digital media, with full chain of custody and destruction documentation.",
    metaDescription:
      "IT and media recycling for organisations across the UK. Secure collection, destruction of hard drives and digital media, WEEE recycling and a full audit trail.",
    highlights: [
      "Recycling in line with WEEE regulations",
      "Hard drive and media destruction",
      "Documentation for your records",
      "Full audit trail issued"
    ],
    sections: [
      {
        heading: "What IT equipment do you collect?",
        bullets: [
          "Laptops & PCs",
          "Servers & data centres",
          "Wires, plugs & cables",
          "Keyboards & mice",
          "Hard drives",
          "Printers",
          "Televisions & computer monitors",
          "Network switches, hubs, routers & firewalls"
        ]
      },
      {
        heading: "Which digital media can you destroy?",
        body: "We provide bespoke solutions for secure media destruction and recycling of used IT equipment, including DVDs, USB flash drives and other digital media. The team will recommend the right WEEE recycling solution for your organisation."
      },
      {
        heading: "Does this service destroy data?",
        body: "Yes. Hard drive destruction makes data completely unrecoverable, frees space in your office, supports regulatory compliance and reduces the risk of fines and penalties."
      },
      {
        heading: "What records will we receive?",
        body: "Our system for the collection and destruction of confidential WEEE, IT and secure media waste helps businesses manage sensitive data in an environmentally friendly way, and a full audit trail is issued for your records."
      },
      {
        heading: "What collection options are there?",
        bullets: [
          "Frequently scheduled WEEE collection & recycling",
          "Ad-hoc IT waste collection & disposal",
          "One-off electronic waste removal"
        ]
      },
      {
        heading: "Which sectors is this service for?",
        body: "Any organisation retiring equipment that has held personal or commercially sensitive data. It is well suited to:",
        bullets: [
          "Healthcare: PCs, laptops and hard drives that held patient or staff records.",
          "Security: laptops, hard drives and digital media holding client, site or staff data.",
          "Logistics: PCs, servers, printers and network equipment from offices and distribution sites.",
          "Hospitality: PCs, servers and network equipment that held guest, booking or payment data."
        ]
      }
    ],
  },
  {
    slug: "product-destruction",
    n: "04",
    title: "Secure Product Destruction",
    tagline:
      "Secure product destruction for unwanted, outdated or counterfeit goods, secure handling, documented destruction and responsible disposal.",
    short:
      "Protect brand integrity with secure, documented destruction of unsold inventory, recalled stock and branded materials. Sustainable downstream routing as standard.",
    metaDescription:
      "Secure product destruction services in the UK. Documented destruction of faulty stock, counterfeit goods, branded items and confidential products.",
    highlights: [
      "Brand & IP protection",
      "Hazardous part decommissioning",
      "Electronic note of secure destruction",
      "Responsible downstream recycling",
    ],
    sections: [
      {
        heading: "Why businesses use product destruction",
        body: "Product destruction protects your brand, prevents fraud and identity theft, and stops sensitive items being diverted to international markets where they could be resold or misused. As security threats evolve, secure product destruction has become a critical component of brand protection.",
      },
      {
        heading: "Faulty products unsuitable for sale",
        body: "Defective or damaged products that cannot be repaired pose a risk to your brand if released into the market. Secure destruction ensures they do not re-enter circulation.",
      },
      {
        heading: "Excess stock (dead stock)",
        body: "Surplus products or items no longer in demand create storage issues and financial strain. Professional destruction helps you manage excess inventory without risking discounted resale or items falling into the wrong hands.",
      },
      {
        heading: "Illegal or counterfeit goods",
        body: "Counterfeit clothing, sunglasses, textiles and pirated media must be removed from circulation to protect consumers and prevent your brand being associated with illicit goods.",
      },
      {
        heading: "Products containing confidential information",
        body: "ID cards, security badges and digital media often hold sensitive personal or corporate information. Destroying these prevents identity theft, fraud and data breaches.",
      },
      {
        heading: "How the process works",
        bullets: [
          "Inspection to confirm items are safe for disposal and identify hazardous components.",
          "Decommissioning of hazardous parts (batteries, chemicals, electrical components) handled safely for people and the environment.",
          "Secure destruction, confirmed by an electronic note of secure destruction, and where possible responsible recycling of the residual material.",
        ],
      },
    ],
  },
  {
    slug: "one-off-shredding",
    n: "05",
    title: "One-off Shredding",
    tagline:
      "A one-off document, textile and IT shredding service, tailored to your requirements, no contract needed.",
    short:
      "Single-collection shredding for office moves, archive clear-outs and end-of-quarter compliance. Same accountability, no contract required.",
    metaDescription:
      "One-off shredding service in the UK for documents, textiles and IT equipment. Ad-hoc collection, secure destruction and recycling, no contract required.",
    highlights: [
      "National coverage",
      "Discreet & secure",
      "Sustainable downstream routing",
      "No contract required",
    ],
    sections: [
      {
        heading: "Book a one-off shredding service",
        body: "We securely collect, handle and dispose of confidential waste, documents, textiles and electronic equipment. Regular shredding contracts aren't always needed; many businesses just need a one-time service to clear space, improve sustainability or stay compliant.",
      },
      {
        heading: "Confidential papers & documents",
        body: "We support businesses and government bodies UK-wide to reclaim space and remain compliant with GDPR and the Data Protection Act 2018, securely collecting, destroying and recycling medical records, financial records and documents containing employee information.",
      },
      {
        heading: "Textiles, workwear & uniforms",
        body: "Left with obsolete workwear after a rebrand? Throwing it out risks impersonation or brand damage. One-off workwear destruction keeps your brand, employees and reputation safe.",
      },
      {
        heading: "IT & electrical waste (WEEE)",
        body: "After an IT upgrade, leftover hard drives, PCs and servers are collected in a single visit, transported securely and shredded off-site at a secure facility, with an electronic note of secure destruction issued.",
      },
      {
        heading: "Product destruction",
        body: "Product that is no longer usable or saleable can be securely collected, destroyed and where possible recycled, issued with the relevant documentation.",
      },
      {
        heading: "End-of-year clear out",
        body: "An end-of-year clear out streamlines operations, reduces inventory cost and prepares the business for a fresh start, freeing valuable space and giving a clear picture going forward.",
      },
    ],
  },
];

export const serviceBySlug = Object.fromEntries(
  services.map((s) => [s.slug, s]),
) as Record<string, Service>;
